/* The characters on this device (up to 5) in the roster, each opened in a CharacterCreator that saves
   to its entry of the list. The page opens on the roster, even an empty one. */

import { useMemo, useState } from 'react';
import CharacterCreator, { SavePdf } from './CharacterCreator';
import CharacterRoster from './CharacterRoster';
import CreationGuide from './CreationGuide';
import type { ContentVersion } from './content/sources';
import { characterStore, loadCharacters, MAX_CHARACTERS } from './storage';

interface Props {
  version: ContentVersion;
  /** A class page's "build this class" link: a new character starts with it, while there is room. */
  startClass?: string;
  /** A class page's blank sheet link: shows that class's sheet to print, keeping nothing. */
  blankClass?: string;
  onBlankBack?: () => void;
  /** Saves Save as PDF's pages as a file; without it, the print window opens. */
  savePdf?: SavePdf;
}

// Which character is open: one of the list by id, a new one (null), or none (the roster).
type Open = string | null | undefined;

export default function CharacterLibrary({ version, startClass, blankClass, onBlankBack, savePdf }: Props) {
  const [list, setList] = useState(loadCharacters);
  const [open, setOpen] = useState<Open>(() =>
    blankClass || (startClass && list.length < MAX_CHARACTERS) ? null : undefined,
  );
  const store = useMemo(() => (open === undefined ? null : characterStore(open)), [open]);

  if (store) {
    const home = () => {
      setOpen(undefined);
      setList(loadCharacters());
    };
    return (
      <CharacterCreator
        key={open ?? 'new'}
        version={version}
        store={store}
        onHome={home}
        startClass={open ? undefined : startClass}
        blankClass={blankClass}
        onBlankBack={onBlankBack}
        savePdf={savePdf}
      />
    );
  }
  return (
    <>
      <CharacterRoster
        version={version}
        characters={list}
        limit={MAX_CHARACTERS}
        onOpen={setOpen}
        onNew={() => setOpen(null)}
        onDelete={(id) => {
          characterStore(id).remove();
          setList(loadCharacters());
        }}
        onImport={(c) => {
          characterStore(null).save(c);
          setList(loadCharacters());
        }}
      />
      <CreationGuide />
    </>
  );
}
